import { useState, useEffect, useMemo } from "react";
import MDEditor from "@uiw/react-md-editor";
import { doc, onSnapshot } from "firebase/firestore";
import { fieldsForCategory, splitOptions, buildAttributes } from "../../utils/productFields";
import { db } from "../../firebase";

/* ================= Drive helpers (for COURSE thumbnail only) ================= */
function extractDriveId(url = "") {
  if (!url) return null;
  return (
    url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/)?.[1] || // /file/d/ID/
    url.match(/[?&]id=([a-zA-Z0-9_-]+)/)?.[1] || // ?id=ID
    url.match(/\/uc\?.*?[?&]id=([a-zA-Z0-9_-]+)/)?.[1] // /uc?export=view&id=ID
  );
}
function getDrivePreviewUrl(url = "") {
  const id = extractDriveId(url);
  return id ? `https://drive.google.com/uc?export=view&id=${id}` : "";
}
function DriveImagePreview({ url }) {
  const [state, setState] = useState("loading"); // 'loading' | 'ok' | 'error'
  const [src, setSrc] = useState(getDrivePreviewUrl(url));

  useEffect(() => {
    const next = getDrivePreviewUrl(url);
    setSrc(next);
    setState(next ? "loading" : "error");
  }, [url]);

  const handleError = () => {
    const id = extractDriveId(url);
    if (id && !String(src || "").includes("/thumbnail")) {
      setSrc(`https://drive.google.com/thumbnail?id=${id}&sz=w1000`);
      setState("loading");
    } else {
      setState("error");
    }
  };

  return (
    <div className="mt-2">
      {state !== "error" && src ? (
        <img
          src={src}
          alt="Main_image_preview"
          className="w-full max-w-md rounded-xl border"
          referrerPolicy="no-referrer"
          onLoad={() => setState("ok")}
          onError={handleError}
        />
      ) : null}
      {state === "error" && (
        <p className="text-xs text-red-500 mt-1">
          Couldn’t load the image. Make sure it’s a direct file from Google
          Drive and sharing is set to <b>“Anyone with the link”</b>.
        </p>
      )}
    </div>
  );
}

/* ================= Catalog options ================= */
const DEFAULT_PRODUCT_CATEGORIES = {
  Electronics: [
    "Mobile Phones",
    "Laptops & Computers",
    "Cameras",
    "Audio & Headphones",
    "Smart Home Devices",
    "Wearable Technology",
  ],
  "Fashion & Apparel": [
    "Men’s Clothing",
    "Women’s Clothing",
    "Kids’ Clothing",
    "Shoes",
    "Accessories (Bags, Jewelry, Watches)",
    "Sportswear",
  ],
  "Home & Living": [
    "Furniture",
    "Home Decor",
    "Kitchen & Dining",
    "Bedding & Bath",
    "Appliances",
  ],
  "Beauty & Health": [
    "Skincare",
    "Haircare",
    "Makeup",
    "Personal Care",
    "Health Supplements",
  ],
  "Sports & Outdoors": [
    "Fitness Equipment",
    "Outdoor Gear",
    "Sportswear",
    "Camping & Hiking",
  ],
  "Toys & Baby Products": [
    "Toys & Games",
    "Baby Clothing",
    "Baby Care",
    "Feeding & Nursing",
  ],
  Automotive: ["Car Accessories", "Motorbike Accessories", "Tools & Equipment"],
  "Books & Stationery": ["Books", "Office Supplies", "Art & Craft Supplies"],
  "Groceries & Gourmet Food": [
    "Fresh Produce",
    "Packaged Foods",
    "Beverages",
    "Organic & Health Foods",
  ],
  Pets: ["Pet Food", "Pet Accessories", "Pet Care"],
  Other: ["Other"],
};

const DEFAULT_SERVICE_CATEGORIES = {
  "Design & Creative": [
    "Logo Design",
    "Branding",
    "UI/UX",
    "Illustration",
    "Video Editing",
    "Photography",
  ],
  "Development & Tech": [
    "Web Development",
    "Mobile Apps",
    "Automation / Scripts",
    "WordPress / CMS",
    "AI Chatbots",
    "Blockchain / Web3",
  ],
  Marketing: [
    "Social Media Management",
    "Paid Ads Setup",
    "SEO",
    "Content Writing",
    "Email Marketing",
    "Analytics & Tagging",
  ],
  "Business & Consulting": [
    "Pitch Decks",
    "Market Research",
    "Business Strategy",
    "Process Automation",
  ],
  Education: ["Coaching", "Tutoring", "Course Creation", "Editing"],
  Other: ["Other"],
};

const CONTACT_CHOICES = ["WhatsApp", "Email", "Phone", "Telegram", "Website"];

function normalizeCategoryMap(input, fallback) {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    return fallback;
  }

  const normalized = Object.entries(input).reduce((acc, [key, value]) => {
    const name = String(key || "").trim();
    if (!name) return acc;
    const options = Array.isArray(value)
      ? value.map((item) => String(item || "").trim()).filter(Boolean)
      : [];
    acc[name] = options.length > 0 ? options : ["Other"];
    return acc;
  }, {});

  return Object.keys(normalized).length > 0 ? normalized : fallback;
}

/* ================= Helpers ================= */
function mdToPlain(md = "") {
  return (md || "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "") // images
    .replace(/\[[^\]]*\]\([^)]*\)/g, "") // links [text](url)
    .replace(/[`*_>#-]/g, "") // formatting tokens
    .replace(/\n+/g, " ") // new lines → spaces
    .trim();
}

/* ================= Component ================= */
export default function StepDetails({
  type,
  values,
  onChange,
  onNext,
  onBack,
  productFields: productFieldDefinitions = [],
}) {
  const [errors, setErrors] = useState({});
  const [productCategories, setProductCategories] = useState(
    DEFAULT_PRODUCT_CATEGORIES,
  );
  const [serviceCategories, setServiceCategories] = useState(
    DEFAULT_SERVICE_CATEGORIES,
  );

  useEffect(() => {
    const ref = doc(db, "listing_settings", "defaults");
    return onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() || {};
        setProductCategories(
          normalizeCategoryMap(
            data.productCategories,
            DEFAULT_PRODUCT_CATEGORIES,
          ),
        );
        setServiceCategories(
          normalizeCategoryMap(
            data.serviceCategories,
            DEFAULT_SERVICE_CATEGORIES,
          ),
        );
      },
      (err) => {
        console.error("listing settings fetch failed", err);
        setProductCategories(DEFAULT_PRODUCT_CATEGORIES);
        setServiceCategories(DEFAULT_SERVICE_CATEGORIES);
      },
    );
  }, []);

  const mainCat = values.category || "";
  const subcats = useMemo(() => {
    if (type === "product") return productCategories[mainCat] || [];
    if (type === "service") return serviceCategories[mainCat] || [];
    return [];
  }, [type, mainCat, productCategories, serviceCategories]);

  const descPlainLen = useMemo(() => {
    if (type === "course") return mdToPlain(values?.description || "").length;
    if (type === "service") return mdToPlain(values?.serviceDesc || "").length;
    return 0;
  }, [type, values?.description, values?.serviceDesc]);

  /* ---------------- Field configs per type ---------------- */
  const courseFields = [
    {
      name: "title",
      label: "Course Title",
      placeholder: "e.g. Mastering Binary Options",
    },
    {
      name: "language",
      label: "Language",
      type: "select",
      options: ["සිංහල", "தமிழ்", "English"],
    },
    {
      name: "thumbnail",
      label: "Main Image (Google Drive link)",
      type: "driveImage",
      placeholder:
        "https://drive.google.com/file/d/FILE_ID/view?usp=share_link",
    },
    {
      name: "category",
      label: "Category",
      type: "select",
      options: [
        "Personal Development",
        "Health & Fitness",
        "Parenting",
        "Cooking & Culinary",
        "Home Improvement & Gardening",
        "Arts & Crafts",
        "Language Learning",
        "Music",
        "Beauty & Makeup",
        "Writing & Literature",
        "Business & Entrepreneurship",
        "Technology & Programming",
        "Fashion & Style",
        "Science & Math",
        "Lifestyle & Hobbies",
        "Artificial Intelligence & Machine Learning",
        "Data Science & Big Data",
        "Blockchain & Cryptocurrency",
        "Cloud Computing",
        "Cybersecurity",
        "Digital Marketing",
        "Remote Work & Productivity",
        "Augmented Reality (AR) & Virtual Reality (VR)",
        "Sustainability & Green Technologies",
        "Personal Finance & Investment (Modern Focus)",
        "Video & Photo Industry",
        "Television and Radio Industry",
      ],
    },
    {
      name: "description",
      label: "Course Description and Course Content",
      type: "markdown",
      placeholder:
        "Outline modules, lessons, outcomes… (Minimum 151 characters, Markdown supported)",
    },
    {
      name: "tags",
      label: "Tags (comma-separated)",
      placeholder: "e.g. web development, fashion designing, music theory",
    },
  ];

  const productFields = [
    {
      name: "title",
      label: "Product Name",
      placeholder: "e.g. Macbook Pro 2020",
    },
    {
      name: "category",
      label: "Main Category",
      type: "select",
      options: Object.keys(productCategories),
    },
    {
      name: "subcategory",
      label: "Subcategory",
      type: "select",
      options: subcats,
    },
    {
      name: "itemType",
      label: "Item Type",
      placeholder: "e.g. T-Shirt, Handbag, Office Chair",
      required: false,
      helper:
        "Add a more specific item type so you can organize products more clearly.",
    },
    {
      name: "stock",
      label: "Available Quantity",
      type: "number",
      min: 0,
      placeholder: "e.g. 25",
      helper:
        "Show buyers how many units you currently have. This stock reduces automatically when orders are placed and returns if an order is declined.",
    },
    {
      name: "tags",
      label: "Tags (comma-separated)",
      placeholder: "e.g. Mobile phones, gift items, fresh flowers",
    },
  ];

  const serviceFields = [
    {
      name: "title",
      label: "Service Name",
      placeholder: "e.g. AI-Powered Website Development",
    },
    {
      name: "category",
      label: "Service Category",
      type: "select",
      options: Object.keys(serviceCategories),
    },
    {
      name: "subcategory",
      label: "Subcategory",
      type: "select",
      options: subcats,
    },
    {
      name: "itemType",
      label: "Item Type",
      placeholder: "e.g. Website Audit, Bridal Makeup, Home Cleaning",
      required: false,
      helper:
        "Add a simple service type so this listing is easier to classify later.",
    },
    {
      name: "serviceDesc",
      label: "Service Description (scope, process, timeline)",
      type: "markdown",
      placeholder:
        "Describe deliverables, workflow, and what the client gets. (Minimum 151 characters)",
    },
    {
      name: "tags",
      label: "Tags (comma-separated)",
      placeholder: "e.g. web design, ecommerce, SEO",
    },
    {
      name: "contactMethods",
      label: "Preferred Contact Methods",
      type: "checkboxes",
      options: CONTACT_CHOICES,
    },
  ];

  const fields =
    type === "course"
      ? courseFields
      : type === "product"
        ? productFields
        : serviceFields;

  /* ---------------- Handlers ---------------- */
  function handleSubmit(e) {
    e.preventDefault();

    if (type === "product") {
      try {
        const attributes = buildAttributes(productFieldDefinitions, values.category, values.productFieldValues, values.attributes || []);
        onChange({ attributes });
        setErrors({});
      } catch (error) {
        setErrors({ productFields: error.message });
        return;
      }
      if (!values.category || !values.subcategory) {
        alert("Please select both a main category and a subcategory.");
        return;
      }
      if (values.stock !== "" && values.stock != null) {
        const stockValue = Number(values.stock);
        if (!Number.isFinite(stockValue) || stockValue < 0) {
          alert("Please enter a valid product quantity of 0 or more.");
          return;
        }
      }
    }

    if (type === "service") {
      if (!values.category) {
        alert("Please select a service category.");
        return;
      }
      const len = mdToPlain(values?.serviceDesc || "").length;
      if (len <= 150) {
        setErrors((p) => ({
          ...p,
          serviceDesc: `Please write at least 151 characters (current: ${len}).`,
        }));
        document
          .getElementById("field-serviceDesc")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      setErrors((p) => ({ ...p, serviceDesc: undefined }));
    }

    if (type === "course") {
      const len = mdToPlain(values?.description || "").length;
      if (len <= 150) {
        setErrors((p) => ({
          ...p,
          description: `Please write at least 151 characters (current: ${len}).`,
        }));
        document
          .getElementById("field-description")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      setErrors((p) => ({ ...p, description: undefined }));
    }

    onNext();
  }

  /* ---------------- Render ---------------- */
  return (
    <main className="h-[calc(95vh-73px)] min-h-0 overflow-y-auto px-6 py-6">
      <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
        {type === "product" && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Keep this step simple. Add the basic product info here, then upload
            your images and write the full description in the next step.
          </div>
        )}
        {fields.map((f) => (
          <label key={f.name} className="font-medium flex flex-col gap-1">
            {f.label}

            {f.type === "driveImage" ? (
              <>
                <input
                  className="w-full p-2 rounded-xl border bg-gray-50"
                  placeholder={f.placeholder}
                  value={values[f.name] ?? ""}
                  onChange={(e) =>
                    onChange({ ...values, [f.name]: e.target.value })
                  }
                  required={f.required !== false}
                  inputMode="url"
                />
                {values[f.name] ? (
                  <DriveImagePreview url={values[f.name]} />
                ) : null}
              </>
            ) : f.type === "select" ? (
              <select
                className="w-full p-2 rounded-xl border bg-gray-50"
                value={values[f.name] ?? ""}
                onChange={(e) => {
                  if (
                    (type === "product" || type === "service") &&
                    f.name === "category"
                  ) {
                    onChange({
                      ...values,
                      category: e.target.value,
                      subcategory: "",
                    });
                  } else {
                    onChange({ ...values, [f.name]: e.target.value });
                  }
                }}
                required={f.required !== false}
              >
                <option value="">Select {f.label.toLowerCase()}…</option>
                {f.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : f.type === "markdown" ? (
              <div className="w-full">
                <div
                  id={
                    f.name === "description"
                      ? "field-description"
                      : f.name === "serviceDesc"
                        ? "field-serviceDesc"
                        : undefined
                  }
                  data-color-mode="light"
                  className={`rounded-xl overflow-hidden border ${
                    (f.name === "description" && errors.description) ||
                    (f.name === "serviceDesc" && errors.serviceDesc)
                      ? "border-red-400"
                      : "border-gray-200"
                  }`}
                >
                  <MDEditor
                    value={values[f.name] || ""}
                    onChange={(val) =>
                      onChange({ ...values, [f.name]: val ?? "" })
                    }
                    height={260}
                    placeholder={f.placeholder}
                  />
                </div>
                {(f.name === "description" || f.name === "serviceDesc") && (
                  <div className="mt-1 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {descPlainLen} / 151
                    </span>
                    {errors[f.name] && (
                      <span className="text-xs text-red-600">
                        {errors[f.name]}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : f.type === "textarea" ? (
              <textarea
                className="w-full p-3 rounded-xl border bg-gray-50 min-h-[120px]"
                placeholder={f.placeholder}
                value={values[f.name] ?? ""}
                onChange={(e) =>
                  onChange({ ...values, [f.name]: e.target.value })
                }
                required={f.required !== false}
              />
            ) : f.type === "number" ? (
              <input
                type="number"
                min={f.min ?? 0}
                step="1"
                className="w-full p-2 rounded-xl border bg-gray-50"
                placeholder={f.placeholder}
                value={values[f.name] ?? ""}
                onChange={(e) =>
                  onChange({
                    ...values,
                    [f.name]:
                      e.target.value === "" ? "" : Number(e.target.value),
                  })
                }
                required={f.required !== false}
              />
            ) : f.type === "checkboxes" ? (
              <div className="flex flex-wrap gap-3">
                {f.options.map((opt) => {
                  const list = Array.isArray(values[f.name])
                    ? values[f.name]
                    : [];
                  const checked = list.includes(opt);
                  return (
                    <label
                      key={opt}
                      className={`px-3 py-2 rounded-xl border cursor-pointer select-none ${
                        checked
                          ? "bg-emerald-50 border-emerald-300"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mr-2 align-middle"
                        checked={checked}
                        onChange={(e) => {
                          const next = new Set(list);
                          if (e.target.checked) next.add(opt);
                          else next.delete(opt);
                          onChange({ ...values, [f.name]: Array.from(next) });
                        }}
                      />
                      {opt}
                    </label>
                  );
                })}
              </div>
            ) : (
              <input
                className="w-full p-2 rounded-xl border bg-gray-50"
                placeholder={f.placeholder}
                value={values[f.name] ?? ""}
                onChange={(e) =>
                  onChange({ ...values, [f.name]: e.target.value })
                }
                required={f.required !== false}
              />
            )}
            {f.helper ? (
              <span className="text-xs leading-5 text-slate-500">
                {f.helper}
              </span>
            ) : null}
          </label>
        ))}

        {type === "product" && mainCat && <section className="rounded-2xl border border-blue-100 bg-blue-50/40 p-4 sm:p-5">
          <div className="mb-5"><p className="text-xs font-bold uppercase tracking-widest text-blue-700">{mainCat}</p><h3 className="mt-1 text-lg font-bold text-slate-900">Product specifications</h3><p className="mt-1 text-sm text-slate-600">Add details buyers need to compare this product. Fields marked * are required.</p></div>
          {fieldsForCategory(productFieldDefinitions, mainCat).length === 0 && <p className="text-sm text-slate-500">No extra specifications are configured for this category.</p>}
          <div className="grid gap-4 sm:grid-cols-2">
        {fieldsForCategory(productFieldDefinitions, mainCat).map((field) => {
          const value = values.productFieldValues?.[field.id] ?? values.attributes?.find((a) => a.id === field.id)?.value ?? "";
          const update = (next) => onChange({ productFieldValues: { ...values.productFieldValues, [field.id]: next } });
          const options = splitOptions(field.options);
          return <fieldset key={field.id} className="min-w-0 rounded-xl border border-slate-200 bg-white p-4">
            <legend className="font-medium">{field.name}{field.required ? " *" : " (optional)"}</legend>
            {field.type === "list" && options.length ? <div className="flex flex-wrap gap-3">{options.map((option) => <label key={option}><input type="checkbox" checked={splitOptions(value).includes(option)} onChange={(e) => update((e.target.checked ? [...splitOptions(value), option] : splitOptions(value).filter((v) => v !== option)).join(", "))} /> {option}</label>)}</div> : <input aria-label={field.name} className="w-full p-2 rounded-xl border bg-gray-50" type={["date", "number"].includes(field.type) ? field.type : "text"} step={field.type === "number" ? "any" : undefined} required={field.required} value={value} onChange={(e) => update(e.target.value)} placeholder={field.type === "list" ? "Separate values with commas" : ""} />}
          </fieldset>;
        })}
          </div></section>}
        {errors.productFields && <p role="alert" className="text-red-600">{errors.productFields}</p>}

        <div className="sticky bottom-0 bg-white/90 backdrop-blur border-t mt-2 pt-2">
          <div className="flex gap-2 justify-end pt-2">
            <button
              type="button"
              className="px-4 py-2 rounded-xl bg-gray-100 text-gray-600"
              onClick={onBack}
            >
              Back
            </button>
            <button
              type="submit"
              className="px-6 py-2 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700"
            >
              Next
            </button>
          </div>
        </div>
      </form>
    </main>
  );
}
